import { Ban,Shield,ShieldOff } from "lucide-react"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { FormDialog } from "@/components/form-dialog"

interface HoldDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  reason: string
  onReasonChange: (value: string) => void
  onConfirm: () => void
  isSubmitting: boolean
}

export function HoldDialog({
  open,
  onOpenChange,
  reason,
  onReasonChange,
  onConfirm,
  isSubmitting,
}: HoldDialogProps) {
  return (
    <FormDialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v)
        if (!v) onReasonChange("")
      }}
      title={
        <div className="flex items-center gap-2 text-amber-600">
          <Shield className="h-5 w-5" />
          Place Inventory on Hold
        </div>
      }
      description="This will temporarily stop all sales for this specific inventory record. A reason is required."
      onConfirm={onConfirm}
      onCancel={() => {
        onOpenChange(false)
        onReasonChange("")
      }}
      confirmLabel={isSubmitting ? "Placing Hold..." : "Place Hold"}
      isPending={isSubmitting}
      isConfirmDisabled={!reason.trim()}
      size="slim"
    >
      <div className="space-y-2">
        <Label htmlFor="stock-hold-reason">Reason</Label>
        <Textarea
          id="stock-hold-reason"
          placeholder="Enter reason for hold..."
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          className="min-h-[100px]"
        />
      </div>
    </FormDialog>
  )
}

interface ReleaseHoldDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  note: string
  onNoteChange: (value: string) => void
  onConfirm: () => void
  isSubmitting: boolean
}

export function ReleaseHoldDialog({
  open,
  onOpenChange,
  note,
  onNoteChange,
  onConfirm,
  isSubmitting,
}: ReleaseHoldDialogProps) {
  return (
    <FormDialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v)
        if (!v) onNoteChange("")
      }}
      title={
        <div className="flex items-center gap-2 text-emerald-600">
          <ShieldOff className="h-5 w-5" />
          Release Inventory Hold
        </div>
      }
      description="This will lift the hold and allow this inventory to be sold again."
      onConfirm={onConfirm}
      onCancel={() => {
        onOpenChange(false)
        onNoteChange("")
      }}
      confirmLabel={isSubmitting ? "Releasing..." : "Release Hold"}
      isPending={isSubmitting}
      isConfirmDisabled={!note.trim()}
      size="slim"
    >
      <div className="space-y-2">
        <Label htmlFor="stock-release-note">Note</Label>
        <Textarea
          id="stock-release-note"
          placeholder="Enter note for releasing hold..."
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          className="min-h-[100px]"
        />
      </div>
    </FormDialog>
  )
}

interface SuspendConfigDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  configLabel: string
  reason: string
  onReasonChange: (value: string) => void
  onConfirm: () => void
  isSubmitting: boolean
}

export function SuspendConfigDialog({
  open,
  onOpenChange,
  configLabel,
  reason,
  onReasonChange,
  onConfirm,
  isSubmitting,
}: SuspendConfigDialogProps) {
  return (
    <FormDialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v)
        if (!v) onReasonChange("")
      }}
      title={
        <div className="flex items-center gap-2 text-red-600">
          <Ban className="h-5 w-5" />
          Suspend Configuration
        </div>
      }
      description={
        <>
          This will temporarily stop sales for the SKU{" "}
          <strong>{configLabel}</strong>.
        </>
      }
      onConfirm={onConfirm}
      onCancel={() => {
        onOpenChange(false)
        onReasonChange("")
      }}
      confirmLabel={isSubmitting ? "Suspending..." : "Suspend"}
      confirmVariant="destructive"
      isPending={isSubmitting}
      isConfirmDisabled={!reason.trim()}
      size="slim"
    >
      <div className="space-y-2">
        <Label htmlFor="stock-suspend-config-reason">Reason</Label>
        <Textarea
          id="stock-suspend-config-reason"
          placeholder="Enter reason for suspension..."
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          className="min-h-[100px]"
        />
      </div>
    </FormDialog>
  )
}

interface ResumeConfigDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  configLabel: string
  note: string
  onNoteChange: (value: string) => void
  onConfirm: () => void
  isSubmitting: boolean
}

export function ResumeConfigDialog({
  open,
  onOpenChange,
  configLabel,
  note,
  onNoteChange,
  onConfirm,
  isSubmitting,
}: ResumeConfigDialogProps) {
  return (
    <FormDialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v)
        if (!v) onNoteChange("")
      }}
      title={
        <div className="flex items-center gap-2 text-emerald-600">
          <ShieldOff className="h-5 w-5" />
          Resume Configuration
        </div>
      }
      description={
        <>
          This will lift the suspension and allow <strong>{configLabel}</strong>{" "}
          to be sold again.
        </>
      }
      onConfirm={onConfirm}
      onCancel={() => {
        onOpenChange(false)
        onNoteChange("")
      }}
      confirmLabel={isSubmitting ? "Resuming..." : "Resume"}
      isPending={isSubmitting}
      isConfirmDisabled={!note.trim()}
      size="slim"
    >
      <div className="space-y-2">
        <Label htmlFor="stock-resume-config-note">Note</Label>
        <Textarea
          id="stock-resume-config-note"
          placeholder="Enter note for resuming..."
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          className="min-h-[100px]"
        />
      </div>
    </FormDialog>
  )
}

